// src/components/dashboard/FeedbackModal.tsx
import React, { useState } from 'react';
import { X, MessageSquare, Star } from 'lucide-react';
import { apiService } from '@/services/api';


interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FeedbackFormData {
  feedback_type: 'bug' | 'feature_request' | 'general' | 'improvement' | 'complaint' | 'praise';
  subject: string;
  message: string;
  rating: number;
  email: string;
  allow_contact: boolean;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FeedbackFormData>({
    feedback_type: 'general',
    subject: '',
    message: '',
    rating: 0,
    email: '',
    allow_contact: true
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 255) {
      newErrors.subject = 'Subject must be less than 255 characters';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Please provide more details (at least 10 characters)';
    }

    if (formData.rating && (formData.rating < 1 || formData.rating > 5)) {
      newErrors.rating = 'Rating must be between 1 and 5';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Gather device/browser info
      const deviceType = /mobile/i.test(navigator.userAgent) ? 'mobile' :
        /tablet/i.test(navigator.userAgent) ? 'tablet' : 'desktop';
      const screenResolution = `${window.screen.width}x${window.screen.height}`;

      const feedbackData = {
        feedback_type: formData.feedback_type,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        rating: formData.rating || undefined,
        page_url: window.location.href,
        screen_resolution: screenResolution,
        device_type: deviceType,
        email: formData.email.trim() || undefined,
        allow_contact: formData.allow_contact
      };

      const response = await apiService.submitFeedback(feedbackData);

      if (response.success) {
        setIsSuccess(true);


        // Close modal after 2 seconds
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.error?.message || 'Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('Feedback submission error:', error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        feedback_type: 'general',
        subject: '',
        message: '',
        rating: 0,
        email: '',
        allow_contact: true
      });
      setErrors({});
      setIsSuccess(false);
      onClose();
    }
  };

  const renderStars = () => {
    return (
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setFormData({ ...formData, rating: star })}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <Star
              size={24}
              fill={star <= formData.rating ? '#F59E0B' : 'none'}
              stroke={star <= formData.rating ? '#F59E0B' : 'var(--color-border)'}
              strokeWidth={2}
            />
          </button>
        ))}
        {formData.rating > 0 && (
          <span style={{
            marginLeft: 'var(--space-2)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-muted)',
            fontFamily: 'var(--font-primary)'
          }}>
            {formData.rating} / 5
          </span>
        )}
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      fontFamily: 'var(--font-primary)',
      padding: 'var(--space-4)'
    }}>
      <div style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius)',
        padding: 'var(--space-6)',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: 'var(--shadow-2xl)',
        border: '1px solid var(--color-border)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div style={{
              backgroundColor: 'var(--color-primary)',
              borderRadius: '50%',
              padding: 'var(--space-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageSquare size={20} color="white" />
            </div>
            <h2 style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-text)',
              margin: 0
            }}>
              Send Feedback
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              padding: 'var(--space-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius)',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = 'var(--color-background)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={24} color="var(--color-muted)" />
          </button>
        </div>

        {/* Success Message */}
        {isSuccess && (
          <div style={{
            backgroundColor: '#ECFDF5',
            border: '1px solid #10B981',
            borderRadius: 'var(--radius)',
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            textAlign: 'center'
          }}>
            <p style={{
              fontSize: 'var(--text-base)',
              color: '#065F46',
              margin: 0,
              fontWeight: 'var(--font-semibold)'
            }}>
              ✓ Thank you for your feedback!
            </p>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: '#059669',
              margin: 'var(--space-2) 0 0 0'
            }}>
              We appreciate you taking the time to help us improve.
            </p>
          </div>
        )}

        {/* Form */}
        {!isSuccess && (
          <form onSubmit={handleSubmit}>
            {/* Feedback Type */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Feedback Type <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <select
                value={formData.feedback_type}
                onChange={(e) => setFormData({ ...formData, feedback_type: e.target.value as any })}
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="general">General Feedback</option>
                <option value="bug">Bug Report</option>
                <option value="feature_request">Feature Request</option>
                <option value="improvement">Improvement Suggestion</option>
                <option value="complaint">Complaint</option>
                <option value="praise">Praise</option>
              </select>
            </div>

            {/* Subject */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Subject <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Brief summary of your feedback"
                disabled={isSubmitting}
                maxLength={255}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: `1px solid ${errors.subject ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!errors.subject) e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  if (!errors.subject) e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              />
              {errors.subject && (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  margin: 'var(--space-1) 0 0 0'
                }}>
                  {errors.subject}
                </p>
              )}
            </div>

            {/* Message */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Message <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Please provide detailed feedback..."
                disabled={isSubmitting}
                rows={6}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: `1px solid ${errors.message ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!errors.message) e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  if (!errors.message) e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              />
              {errors.message && (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  margin: 'var(--space-1) 0 0 0'
                }}>
                  {errors.message}
                </p>
              )}
            </div>

            {/* Rating */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Rating (Optional)
              </label>
              {renderStars()}
              <p style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-muted)',
                margin: 'var(--space-2) 0 0 0'
              }}>
                How would you rate your overall experience?
              </p>
            </div>

            {/* Email */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-text)',
                marginBottom: 'var(--space-2)'
              }}>
                Email (Optional)
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  fontSize: 'var(--text-base)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: `1px solid ${errors.email ? 'var(--color-error)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-primary)',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!errors.email) e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  if (!errors.email) e.currentTarget.style.borderColor = 'var(--color-border)';
                }}
              />
              {errors.email && (
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  margin: 'var(--space-1) 0 0 0'
                }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Allow Contact Checkbox */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={formData.allow_contact}
                  onChange={(e) => setFormData({ ...formData, allow_contact: e.target.checked })}
                  disabled={isSubmitting}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text)',
                  fontFamily: 'var(--font-primary)'
                }}>
                  You may contact me about this feedback
                </span>
              </label>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-text)',
                  backgroundColor: 'var(--color-background)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = 'var(--color-background)';
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="submit-feedback-button"
                style={{
                  padding: 'var(--space-3) var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  fontFamily: 'var(--font-primary)',
                  color: 'var(--color-background)',
                  backgroundColor: 'var(--color-text)',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <span>Submitting...</span>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid var(--color-background)',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  </>
                ) : (
                  <>
                    <MessageSquare size={16} />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
